import { useI18n } from '@ecom/i18n';
import { LanguageSwitcher } from '@ecom/ui-kit';
import { motion } from 'framer-motion';
import { type ReactElement, type ReactNode } from 'react';

import { useLocale } from '@/lib/locale';

/**
 * Identical to apps/merchant-dashboard's own AuthLayout.tsx, plus a
 * `LanguageSwitcher` — unlike the authenticated shell, nothing here can
 * rely on the Topbar for a language choice, so this shared pre-auth
 * layout (Login + the password change) carries its own.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
  footer?: ReactNode;
}): ReactElement {
  const { t } = useI18n();
  const { locale, setLocale } = useLocale();

  return (
    <div className="flex min-h-dvh items-center justify-center bg-ground px-4 py-10">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="w-full max-w-md"
      >
        <div className="mb-4 flex items-center justify-center">
          <LanguageSwitcher
            locale={locale}
            onChange={(next) => {
              setLocale(next);
            }}
          />
        </div>

        <div className="mb-6 flex items-center justify-center gap-2">
          <span
            className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent font-display text-xl font-bold text-white"
            aria-hidden="true"
          >
            S
          </span>
          <span className="font-display text-xl font-bold text-ink">
            {t('merchant.sidebar.brand')}
          </span>
        </div>

        <div className="rounded-2xl border border-line bg-surface p-7 shadow-card">
          <h1 className="font-display text-xl font-bold text-ink">{title}</h1>
          <p className="mt-1 text-sm text-inkFaint">{subtitle}</p>
          <div className="mt-6">{children}</div>
        </div>

        {footer !== undefined ? (
          <div className="mt-5 text-center text-sm text-inkFaint">{footer}</div>
        ) : null}
      </motion.div>
    </div>
  );
}
